import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, TextField } from '@mui/material';
import { InlineSpinner } from '../design-system/components/InlineSpinner';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { configService } from '../services/configService';
import { toast } from '../utils/toast';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ns = 'bugReportModal';

const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('settings');
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const contextInfoHtml = useMemo(() => {
    const now = new Date();
    const dateTime = now.toLocaleString(i18n.language, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

    const userInfo = `
      <ul>
        <li><strong>${t(`${ns}.email.userId`)}</strong>: ${user?.user_id ?? t(`${ns}.email.notLoggedIn`)}</li>
        <li><strong>${t(`${ns}.email.name`)}</strong>: ${user?.name ?? ''}</li>
        <li><strong>${t(`${ns}.email.email`)}</strong>: ${user?.email ?? ''}</li>
        <li><strong>${t(`${ns}.email.proId`)}</strong>: ${user?.pro_id ?? ''}</li>
      </ul>
    `;

    return `
      <p><strong>${t(`${ns}.email.dateTime`)}</strong>: ${dateTime} (${timezone})</p>
      <p><strong>${t(`${ns}.email.page`)}</strong>: ${pageUrl}</p>
      <p><strong>${t(`${ns}.email.browser`)}</strong>: ${userAgent}</p>
      <p><strong>${t(`${ns}.email.user`)}</strong>:</p>
      ${userInfo}
    `;
  }, [user, t, i18n.language]);

  const sendEmail = async (toEmail: string, subject: string, htmlContent: string) => {
    const endpoint = configService.getFullApiUrl(`/send-email`);
    const token = localStorage.getItem('token');
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ type: 'general', toEmail, subject, htmlContent }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || t(`${ns}.emailSendFailed`));
    }
  };

  const handleSubmit = async () => {
    const messageText = message.trim();
    if (!messageText) {
      setMessageError(true);
      return;
    }
    setMessageError(false);

    try {
      setSubmitting(true);

      const subject = t(`${ns}.emailSubject`);
      const htmlContent = `
        <div>
          <p><strong>${t(`${ns}.email.messageHeading`)}</strong>:</p>
          <div style="white-space:pre-wrap">${messageText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
          <hr />
          <h4>${t(`${ns}.email.contextHeading`)}</h4>
          ${contextInfoHtml}
        </div>
      `;

      await Promise.all([
        sendEmail('contact@rintintin-booking.com', subject, htmlContent),
        sendEmail('akseelmh@gmail.com', subject, htmlContent),
      ]);

      toast.success(t(`${ns}.success`));
      setMessage('');
      onClose();
    } catch {
      toast.error(t(`${ns}.sendError`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: 12 }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        fontWeight: 600,
        borderBottom: '1px solid #f0f0f0'
      }}>
        {t(`${ns}.title`)}
        <IconButton
          aria-label={t(`${ns}.closeAriaLabel`)}
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <TextField
          label={t(`${ns}.messageLabel`)}
          placeholder={t(`${ns}.messagePlaceholder`)}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (messageError) setMessageError(false);
          }}
          error={messageError}
          helperText={messageError ? t(`${ns}.messageRequired`) : undefined}
          multiline
          rows={5}
          fullWidth
          required
          margin="normal"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9', borderTop: '1px solid #f0f0f0' }}>
        <Button onClick={onClose} color="inherit">
          {t(`${ns}.cancel`)}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={submitting}
          startIcon={submitting ? <InlineSpinner color="inherit" aria-hidden /> : undefined}
        >
          {t(`${ns}.send`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugReportModal;
